import React, { useState, useRef, useEffect } from 'react';
import { FaStar, FaYelp, FaGoogle, FaTripadvisor, FaChevronDown, FaExternalLinkAlt } from 'react-icons/fa';

/**
 * ReviewLinks Component
 * Provides quick access to reviews from Google, Yelp, and TripAdvisor for any location.
 */
const ReviewLinks = ({ name, location, coordinates, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate search URLs for different platforms
  const getGoogleMapsUrl = () => {
    if (coordinates && coordinates.lat && coordinates.lng) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}&query_place_id=`;
    }
    const searchQuery = location ? `${name} ${location}` : name;
    return `https://www.google.com/maps/search/${encodeURIComponent(searchQuery)}`;
  };

  const getYelpUrl = () => {
    const searchQuery = encodeURIComponent(name);
    const locationQuery = location ? encodeURIComponent(location) : '';
    return `https://www.yelp.com/search?find_desc=${searchQuery}&find_loc=${locationQuery}`;
  };

  const getTripAdvisorUrl = () => {
    const searchQuery = location ? `${name} ${location}` : name;
    return `https://www.tripadvisor.com/Search?q=${encodeURIComponent(searchQuery)}`;
  };

  const reviewSources = [
    {
      name: 'Google',
      icon: FaGoogle,
      url: getGoogleMapsUrl(),
      color: 'text-blue-600 hover:text-blue-700',
      bgColor: 'hover:bg-blue-50'
    },
    {
      name: 'Yelp',
      icon: FaYelp,
      url: getYelpUrl(),
      color: 'text-red-600 hover:text-red-700',
      bgColor: 'hover:bg-red-50'
    },
    {
      name: 'TripAdvisor',
      icon: FaTripadvisor,
      url: getTripAdvisorUrl(),
      color: 'text-green-600 hover:text-green-700',
      bgColor: 'hover:bg-green-50'
    }
  ];

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors"
        title={`View reviews for ${name}`}
      >
        <FaStar className="text-amber-500" size={10} />
        Reviews
        <FaChevronDown size={8} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-44 bg-white rounded-lg shadow-lg border border-neutral-300 py-1 left-0">
          <div className="px-3 py-2 border-b border-neutral-100">
            <p className="text-xs font-semibold text-neutral-600 truncate" title={name}>
              {name}
            </p>
          </div>
          {reviewSources.map((source) => (
            <a
              key={source.name}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`flex items-center gap-2 px-3 py-2 text-sm ${source.color} ${source.bgColor} transition-colors`}
            >
              <source.icon size={14} />
              <span>{source.name} Reviews</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewLinks;
